import React, {memo, ReactNode, useCallback, useMemo} from 'react';
import {AirConditionerState, MediaModel} from '../../../types/control';

import {useRPC} from '../../../hooks/rpc';
import {AirConditionerControlComponent, MediaOtherControl} from './component';

type Props = {
    media: MediaModel;
    children?: ReactNode;
};

export const Media = memo(function AirConditionerControl({media, children}: Props) {
    const [rpc] = useRPC();
    const mediaButtonPressed = useCallback((slot: number) => rpc.mediaButtonPressed(media.id, slot), [rpc, media]);
    const airClick = useCallback((isOpen: boolean) => mediaButtonPressed(isOpen ? 1 : 0), [mediaButtonPressed]);

    const createMinusPlus = useCallback((min: number, max: number, offset: number, value: number) => {
        const fn = (isMinus: boolean) => () => {
            if (isMinus) {
                value -= 1;
                if (value <= min) {
                    value = min;
                }
            } else {
                value += 1;
                if (value >= max) {
                    value = max;
                }
            }

            mediaButtonPressed(offset + value);
        };

        return [fn(true), fn(false)];
    }, [mediaButtonPressed]);

    const [airFanMinus, airFanPlus] = useMemo(() => {
        if (media.type_id === '1') {
            return createMinusPlus(0, 4, 8, (media.state as AirConditionerState).fan_speed);
        }

        return [null, null];
    }, [createMinusPlus, media]);
    const [airFlapMinus, airFlapPlus] = useMemo(() => {
        if (media.type_id === '1') {
            return createMinusPlus(0, 4, 13, (media.state as AirConditionerState).flap_speed);
        }

        return [null, null];
    }, [createMinusPlus, media]);
    const [airMinus, airPlus] = useMemo(() => {
        if (media.type_id === '1') {
            return createMinusPlus(15, 32, 3, (media.state as AirConditionerState).temperature);
        }

        return [null, null];
    }, [createMinusPlus, media]);

    const otherClick = useCallback(() => mediaButtonPressed(0), [mediaButtonPressed]);
    const otherPlus = useCallback(() => mediaButtonPressed(1), [mediaButtonPressed]);
    const otherMinus = useCallback(() => mediaButtonPressed(2), [mediaButtonPressed]);
    const otherVolumePlus = useCallback(() => mediaButtonPressed(6), [mediaButtonPressed]);
    const otherVolumeMinus = useCallback(() => mediaButtonPressed(7), [mediaButtonPressed]);
    const otherMute = useCallback(() => mediaButtonPressed(3), [mediaButtonPressed]);
    const otherPlay = useCallback(() => mediaButtonPressed(8), [mediaButtonPressed]);

    if (media.type_id === '1') {
        return (
            <AirConditionerControlComponent
                media={media as MediaModel<AirConditionerState>}
                airFlapMinus={airFlapMinus}
                airFlapPlus={airFlapPlus}
                fanMinus={airFanMinus}
                click={airClick}
                fanPlus={airFanPlus}
                minus={airMinus}
                plus={airPlus}>
                {children}
            </AirConditionerControlComponent>
        );
    }

    return (
        <MediaOtherControl
            media={media}
            click={otherClick}
            plus={otherPlus}
            minus={otherMinus}
            volumePlus={otherVolumePlus}
            volumeMinus={otherVolumeMinus}
            mute={otherMute}
            play={otherPlay}>
            {children}
        </MediaOtherControl>
    );
});
